if (!customElements.get('delmicure-fullscreen-hero')) {
  class DelmicureFullscreenHero extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-hero-dot]'));
      this.pauseControl = this.querySelector('[data-hero-pause]');
      this.status = this.querySelector('[data-hero-status]');
      this.index = 0;
      this.visible = true;
      this.focusPaused = false;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = this.motion.matches || this.dataset.rotate !== 'true';
      this.interval = Math.max(5000, Number(this.dataset.interval) || 6000);
      this.videoItems = this.slides.map((slide) => {
        const video = slide.querySelector('video');
        const control = slide.querySelector('[data-hero-video-control]');
        if (!video || !control) return null;
        const item = { slide, video, control, userPaused: false };
        video.muted = true;
        video.playsInline = true;
        item.update = () => {
          const label = video.paused ? control.dataset.playLabel : control.dataset.pauseLabel;
          control.classList.toggle('is-paused', video.paused);
          control.setAttribute('aria-label', label);
          control.querySelector('[data-hero-control-label]').textContent = label;
        };
        item.playing = () => { slide.dataset.videoReady = 'true'; item.update(); };
        item.error = () => { delete slide.dataset.videoReady; control.hidden = true; };
        item.toggle = () => {
          this.userPaused = true;
          if (video.paused) { item.userPaused = false; video.play().catch(item.update); }
          else { item.userPaused = true; video.pause(); }
          this.updateRotation();
        };
        video.addEventListener('playing', item.playing);
        video.addEventListener('pause', item.update);
        video.addEventListener('error', item.error);
        control.addEventListener('click', item.toggle);
        control.hidden = false;
        item.update();
        return item;
      }).filter(Boolean);
      this.onClick = (event) => {
        if (event.target.closest('[data-hero-prev]')) this.show(this.index - 1, true);
        else if (event.target.closest('[data-hero-next]')) this.show(this.index + 1, true);
        else if (event.target.closest('[data-hero-dot]')) this.show(Number(event.target.closest('[data-hero-dot]').dataset.heroDot), true);
        else if (event.target.closest('[data-hero-pause]')) {
          this.userPaused = !this.userPaused;
          if (!this.userPaused) this.focusPaused = false;
          this.updateRotation();
        }
      };
      this.onKey = (event) => {
        if (!event.target.closest('.dc-hero-controls')) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.show(this.index + (event.key === 'ArrowRight' ? 1 : -1), true);
        }
      };
      this.onFocus = (event) => { this.focusPaused = event.target.matches(':focus-visible'); this.schedule(); };
      this.onBlur = (event) => { if (!this.contains(event.relatedTarget)) { this.focusPaused = false; this.schedule(); } };
      this.onMotion = () => { if (this.motion.matches) this.userPaused = true; this.syncVideos(); this.updateRotation(); };
      this.onVisibility = () => { this.syncVideos(); this.schedule(); };
      this.onBlock = (event) => {
        const slide = event.target.closest('[data-hero-slide]');
        if (slide && this.contains(slide)) this.show(this.slides.indexOf(slide), true);
      };
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKey);
      this.addEventListener('focusin', this.onFocus);
      this.addEventListener('focusout', this.onBlur);
      this.motion.addEventListener('change', this.onMotion);
      document.addEventListener('visibilitychange', this.onVisibility);
      document.addEventListener('shopify:block:select', this.onBlock);
      this.observer = new IntersectionObserver((entries) => {
        this.visible = entries[0].isIntersecting;
        this.syncVideos();
        this.schedule();
      }, { threshold: 0.1 });
      this.observer.observe(this);
      this.show(0, false);
    }
    show(index, manual) {
      if (!this.slides.length) return;
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, position) => {
        const active = position === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.toggleAttribute('inert', !active);
      });
      this.dots.forEach((dot, position) => dot.setAttribute('aria-current', String(position === this.index)));
      const upcomingImage = this.slides[(this.index + 1) % this.slides.length].querySelector('img');
      if (upcomingImage) upcomingImage.loading = 'eager';
      if (manual) {
        if (this.status) this.status.textContent = this.slides[this.index].getAttribute('aria-label');
      }
      this.syncVideos();
      this.updateRotation();
    }
    syncVideos() {
      this.videoItems.forEach((item) => {
        if (item.slide === this.slides[this.index] && this.visible && !document.hidden && !this.motion.matches && item.slide.dataset.autoplay === 'true' && !item.userPaused) item.video.play().catch(item.update);
        else item.video.pause();
      });
    }
    updateRotation() {
      if (this.pauseControl) {
        this.pauseControl.classList.toggle('is-paused', this.userPaused);
        this.pauseControl.setAttribute('aria-label', this.userPaused ? this.pauseControl.dataset.playLabel : this.pauseControl.dataset.pauseLabel);
      }
      this.schedule();
    }
    schedule() {
      clearTimeout(this.timer);
      if (this.slides.length < 2 || this.userPaused || this.focusPaused || !this.visible || document.hidden || this.dataset.designMode === 'true') return;
      this.timer = setTimeout(() => this.show(this.index + 1, false), this.interval);
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.observer?.disconnect();
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKey);
      this.removeEventListener('focusin', this.onFocus);
      this.removeEventListener('focusout', this.onBlur);
      this.motion?.removeEventListener('change', this.onMotion);
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.removeEventListener('shopify:block:select', this.onBlock);
      this.videoItems?.forEach((item) => {
        item.video.pause();
        item.video.removeEventListener('playing', item.playing);
        item.video.removeEventListener('pause', item.update);
        item.video.removeEventListener('error', item.error);
        item.control.removeEventListener('click', item.toggle);
      });
      this.initialized = false;
    }
  }
  customElements.define('delmicure-fullscreen-hero', DelmicureFullscreenHero);
}
